import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Tabs,
  tabId,
  tabPanelId,
  categoriaLabel,
} from '@aions/ui-primitives';
import { apiFetch } from '../lib/auth-client';
import { useSession } from '../contexts/SessionContext';
import type { RecursoCaseResponse } from '../lib/case-wire';

type Tab = 'descricao' | 'pareceres_1a' | 'decisao_1a' | 'recurso' | 'oitivas';

interface RecordExtras {
  /** Membros que votaram em 1ª instância — entregue pelo backend para REC-02 hint
   *  (se ausente do response, o frontend não esconde — backend continua source of truth). */
  membrosVotaram1aInstancia?: ReadonlyArray<string>;
}

export function CaseDetailPage({
  caseId,
  onBack,
  onParecer,
  onDecisao,
}: {
  caseId: string;
  onBack: () => void;
  onParecer: (id: string) => void;
  onDecisao: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { session } = useSession();
  const [data, setData] = useState<(RecursoCaseResponse & RecordExtras) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('descricao');

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    void (async () => {
      try {
        const resp = await apiFetch(`/api/recurso/cases/${caseId}`);
        if (cancelled) return;
        if (resp.status === 403) {
          setError(t('parecer.errors.forbidden_membrosVotaram1aInstancia'));
          return;
        }
        if (!resp.ok) {
          setError(t('common.error_unknown'));
          return;
        }
        const json = (await resp.json()) as RecursoCaseResponse & RecordExtras;
        setData(json);
      } catch {
        if (!cancelled) setError(t('common.error_network'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  // ---- UX hints (REC-02 + D-120 — backend continua source of truth) ----
  const userId = session?.user.id;
  const interpostoPorSelf =
    userId !== undefined && data?.recurso?.interpostoPor !== undefined
      ? data.recurso.interpostoPor === userId
      : false;
  const votou1a =
    userId !== undefined && Array.isArray(data?.membrosVotaram1aInstancia)
      ? (data.membrosVotaram1aInstancia ?? []).includes(userId)
      : false;
  const blockReason: 'rec02' | 'self' | null = votou1a
    ? 'rec02'
    : interpostoPorSelf
      ? 'self'
      : null;

  // Quórum counter para D-119 — passa para a página de Decisão.
  const quorumCount = data?.recurso?.pareceres.length ?? 0;
  const quorumOk = quorumCount >= 2;

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 16 }}>
        ← {t('case_detail.back')}
      </Button>

      {error && <Alert variant="error">{error}</Alert>}

      {data && (
        <Card>
          <div className="case-head">
            <div className="case-head__titles">
              {/* Título humano (a categoria) como headline; caseId truncado vira
                  etiqueta mono secundária. A categoria deixa de usar a pílula de
                  gravidade (semântica errada — design v2). */}
              <h1 className="case-head__title">{categoriaLabel(t, data.categoria)}</h1>
              <span className="case-head__id">{data.caseId.slice(0, 16)}…</span>
              <div className="case-head__badges">
                <Badge tone="brand">{data.state.kind}</Badge>
                {data.recurso?.origem && (
                  <Badge tone="info">
                    {t('case_detail.recurso_origem')}:{' '}
                    {data.recurso.origem === 'denunciante'
                      ? t('dashboard.origem_denunciante')
                      : t('dashboard.origem_denunciado_via_rh')}
                  </Badge>
                )}
              </div>
            </div>
            <div className="case-head__actions">
              {/* Botão Parecer — esconde via REC-02 + D-120 hints */}
              {blockReason === null ? (
                <Button variant="ghost" size="sm" onClick={() => onParecer(caseId)}>
                  {t('case_detail.btn_emitir_parecer')}
                </Button>
              ) : (
                <Alert variant="warning" role="note" style={{ margin: 0 }}>
                  ⚠{' '}
                  {blockReason === 'rec02'
                    ? t('case_detail.btn_emitir_parecer_blocked_rec02')
                    : t('case_detail.btn_emitir_parecer_blocked_self')}
                </Alert>
              )}

              {blockReason === null && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onDecisao(caseId)}
                  disabled={!quorumOk}
                  title={
                    quorumOk ? t('decisao.quorum_ok') : t('decisao.errors.pareceres_insuficientes')
                  }
                >
                  {t('case_detail.btn_emitir_decisao')}
                </Button>
              )}

              <Badge
                tone={quorumOk ? 'success' : 'amber'}
                aria-label={t('decisao.quorum_counter', { count: quorumCount, max: 3 })}
              >
                {t('decisao.quorum_counter', { count: quorumCount, max: 3 })}
              </Badge>
            </div>
          </div>

          <Tabs<Tab>
            aria-label={t('case_detail.tabs_label')}
            value={tab}
            onValueChange={setTab}
            tabs={[
              { value: 'descricao', label: t('case_detail.tab_descricao') },
              { value: 'decisao_1a', label: t('case_detail.tab_decisao_1a') },
              { value: 'recurso', label: t('case_detail.tab_recurso') },
              { value: 'pareceres_1a', label: t('case_detail.tab_pareceres_1a') },
              { value: 'oitivas', label: t('case_detail.tab_oitivas') },
            ]}
          />

          {tab === 'descricao' && (
            <div
              role="tabpanel"
              id={tabPanelId('descricao')}
              aria-labelledby={tabId('descricao')}
              tabIndex={0}
            >
              <Alert variant="info" role="status" style={{ marginBottom: 12 }}>
                {data.dekWrappedByComite
                  ? t('case_detail.encrypted_loading')
                  : t('case_detail.decrypt_failed')}
              </Alert>
              <div className="review-section">
                <div className="review-label">{t('case_detail.criado_em')}</div>
                <div className="review-value">{data.createdWeek}</div>
              </div>
              <div className="review-section">
                <div className="review-label">{t('case_detail.categoria')}</div>
                <div className="review-value">{categoriaLabel(t, data.categoria)}</div>
              </div>
              <div className="review-section">
                <div className="review-label">
                  {t('case_detail.anexos_count', { count: data.attachments.length })}
                </div>
              </div>
              {/* Dump cru de ciphertext (base64) removido — não deve aparecer
                  pro humano (design v2, correções pontuais de UX). */}
            </div>
          )}

          {tab === 'decisao_1a' && (
            <div
              role="tabpanel"
              id={tabPanelId('decisao_1a')}
              aria-labelledby={tabId('decisao_1a')}
              tabIndex={0}
            >
              {data.decisaoPrimeiraInstancia ? (
                <>
                  <div className="review-section">
                    <div className="review-label">{t('case_detail.decisao_1a_kind')}</div>
                    <div className="review-value">{data.decisaoPrimeiraInstancia.kind}</div>
                  </div>
                  <div className="review-section">
                    <div className="review-label">{t('case_detail.carta_denunciado_1a')}</div>
                    <div className="review-value">
                      {data.decisaoPrimeiraInstancia.cartaParaDenunciado}
                    </div>
                  </div>
                </>
              ) : (
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  {t('case_detail.no_decisao_1a')}
                </p>
              )}
            </div>
          )}

          {tab === 'recurso' && (
            <div
              role="tabpanel"
              id={tabPanelId('recurso')}
              aria-labelledby={tabId('recurso')}
              tabIndex={0}
            >
              {data.recurso ? (
                <>
                  <div className="review-section">
                    <div className="review-label">{t('case_detail.recurso_origem')}</div>
                    <div className="review-value">
                      {data.recurso.origem === 'denunciante'
                        ? t('dashboard.origem_denunciante')
                        : t('dashboard.origem_denunciado_via_rh')}
                    </div>
                  </div>
                  <div className="review-section">
                    <div className="review-label">{t('case_detail.recurso_interposto_em')}</div>
                    <div className="review-value">
                      {new Date(data.recurso.interpostoEm).toLocaleString('pt-BR')}
                    </div>
                  </div>
                  {/* Dump cru do ciphertext da justificativa removido — mesmo
                      anti-padrão do descricao (design v2). */}
                </>
              ) : (
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  {t('case_detail.no_recurso')}
                </p>
              )}
            </div>
          )}

          {tab === 'pareceres_1a' && (
            <div
              role="tabpanel"
              id={tabPanelId('pareceres_1a')}
              aria-labelledby={tabId('pareceres_1a')}
              tabIndex={0}
            >
              {data.decisaoPrimeiraInstancia ? (
                <>
                  <h2 style={{ fontSize: '1rem', marginBottom: 12 }}>
                    {t('case_detail.votacao_1a_titulo')}
                  </h2>
                  <div className="review-section">
                    <div className="review-label">{t('case_detail.voto_procedente')}</div>
                    <div className="review-value">
                      {data.decisaoPrimeiraInstancia.votoTally?.procedente ?? 0}
                    </div>
                  </div>
                  <div className="review-section">
                    <div className="review-label">{t('case_detail.voto_improcedente')}</div>
                    <div className="review-value">
                      {data.decisaoPrimeiraInstancia.votoTally?.improcedente ?? 0}
                    </div>
                  </div>
                  <div className="review-section">
                    <div className="review-label">
                      {t('case_detail.voto_improcedente_insuficiencia')}
                    </div>
                    <div className="review-value">
                      {data.decisaoPrimeiraInstancia.votoTally?.improcedente_insuficiencia ?? 0}
                    </div>
                  </div>
                  <div className="review-section">
                    <div className="review-label">{t('case_detail.decisao_1a_kind')}</div>
                    <div className="review-value">{data.decisaoPrimeiraInstancia.kind}</div>
                  </div>
                  {data.decisaoPrimeiraInstancia.emittedAt !== undefined && (
                    <div className="review-section">
                      <div className="review-label">{t('case_detail.votacao_emitida_em')}</div>
                      <div className="review-value">
                        {new Date(data.decisaoPrimeiraInstancia.emittedAt).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  {t('case_detail.sem_decisao_1a')}
                </p>
              )}
            </div>
          )}

          {tab === 'oitivas' && (
            <div
              role="tabpanel"
              id={tabPanelId('oitivas')}
              aria-labelledby={tabId('oitivas')}
              tabIndex={0}
            >
              <p style={{ color: 'var(--color-text-secondary)' }}>
                {t('case_detail.oitivas_indisponivel')}
              </p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
